/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { httpService } from 'src/core/services/api/httpService';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { IntentSwagger, LabelsPerIntentSwagger } from 'src/luis/core/services/intents/intentSwagger';
import { LuisId } from 'src/luis/types/Resource';
import { DomainModel } from 'src/luis/types/stores/domains/DomainStore';

/**
 * Represents a service to perform CRUD operations for intents.
 */
export const getIntentService = createService(({ context, getEnvironment }) => {
    const { apiUrl, webApiUrl, isProduction } = getEnvironment();
    const apiOptions = luisRequestOptionsBuilder()
        .useDefaultOptions(context.serviceKey, isProduction)
        .build();

    /**
     * Creates a common url scheme for this service.
     *
     * @param intentId An optional intent id to add.
     * @param deleteUtterances An optional whether to delete utterances
     * on intent delete or not.
     * @returns A url string to call.
     */
    const getUrlBuilder = (intentId?: LuisId) =>
        urlBuilder()
            .baseUrl(apiUrl)
            .app(context.appId)
            .version(context.versionId)
            .resource('intents', intentId);

    /**
     * Gets a list of intents in the application.
     */
    const getIntents = async (): Promise<IntentSwagger[]> => {
        const url = urlBuilder()
            .baseUrl(webApiUrl)
            .app(context.appId)
            .version(context.versionId)
            .resource('detailedModels')
            .queryParam('modelFilterType', 'Intent')
            .queryParam('take', `${LuisConstants.ResourceLimits.ServiceFetchSizeLimit}`)
            .build();

        const { data: intents } = await httpService.get<IntentSwagger[]>(url, apiOptions);

        return intents;
    };

    /**
     * Gets an intent by id.
     *
     * @param id The id of the intent to fetch.
     */
    const getIntentById = async ({ id }: Pick<IntentSwagger, 'id'>): Promise<IntentSwagger> => {
        const { data: intent } = await httpService.get<IntentSwagger>(getUrlBuilder(id).build(), apiOptions);

        return intent;
    };

    /**
     * Creates a new intent.
     *
     * @param param0 An object that contains the intent name.
     * @returns A new intent store.
     */
    const createIntent = async ({ name }: Pick<IntentSwagger, 'name'>): Promise<string> => {
        const { data: intentId } = await httpService.post<string>(getUrlBuilder().build(), { name }, apiOptions);

        return intentId;
    };

    /**
     * Add the prebuilt domain intents to the current app.
     *
     * @param prebuiltDomain The prebuilt domain to be added.
     */
    const addPrebuiltDomainIntents = async (prebuiltDomains: Pick<DomainModel, 'domainName' | 'modelName'>[]): Promise<string[]> => {
        const url = urlBuilder()
            .baseUrl(webApiUrl)
            .app(context.appId)
            .version(context.versionId)
            .resource('customprebuiltintentsbatch')
            .build();

        const { data: intentIds } = await httpService.post<string[]>(url, { requestedIntents: prebuiltDomains }, apiOptions);

        return intentIds;
    };

    /**
     * Renames the given intent.
     *
     * @param id The id of the intent to rename.
     * @param name The new name to give the intent.
     */
    const renameIntent = async ({ id, name }: Pick<IntentSwagger, 'id' | 'name'>): Promise<void> => {
        await httpService.put(getUrlBuilder(id).build(), { name }, apiOptions);
    };

    /**
     * Deletes the intent with the given id.
     *
     * @param id The id of the intent to delete.
     * @param deleteUtterances A flag to whether the delete the utterances or not.
     */
    const deleteIntent = async ({
        id,
        deleteUtterances = false
    }: Pick<IntentSwagger, 'id'> & { deleteUtterances: boolean }): Promise<void> => {
        await httpService.delete(
            getUrlBuilder(id)
                .queryParam('deleteUtterances', `${deleteUtterances}`)
                .build(),
            apiOptions
        );
    };

    /**
     * Gets a map of intent ids and their respective
     * labels count.
     */
    const getLabelsPerIntentCount = async (): Promise<LabelsPerIntentSwagger> => {
        const url = urlBuilder()
            .baseUrl(webApiUrl)
            .app(context.appId)
            .version(context.versionId)
            .resource('stats')
            .resource('labelsPerIntent')
            .build();

        const { data } = await httpService.get<LabelsPerIntentSwagger>(url, apiOptions);

        return data;
    };

    return { getIntents, getIntentById, createIntent, renameIntent, deleteIntent, getLabelsPerIntentCount, addPrebuiltDomainIntents };
});
